import { FC, useCallback, useEffect, useRef, useState } from "react";
import { IconButton, Stack } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useFlag } from "@unleash/proxy-client-react";
import { BillingExplorerGroupBySelector } from "./components/BillingExplorerGroupBySelector";
import { ResourceExplorerDateRange } from "./components/ResourceExplorerDateRange";
import { ResourceExplorerFilterTree } from "./components/filter-tree/ResourceExplorerFilterTree";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { isBillingAvailableSelector } from "@/store/common/selectors/billing-status/isBillingAvailableSelector";
import { ExploreWithAIButtonV2 } from "@/storybook/explore-with-AI-button/ExploreWithAIButtonV2";
import { setExplainWithAiMode } from "@/store/explain-with-ai/explainWithAiSlice";
import { setBillingExplorerExplainWithAIDataThunk } from "@/store/resource-explorer/thunks/explain-with-ai/setBillingExplorerExplainWithAIDataThunk";
import {
  setBillingExplorerSideBarPanelContentType,
  setForecastSuggestionPopoverOpen,
} from "@/store/resource-explorer/resourceExplorerSlice";
import { ResourceExplorerForecastSuggestionPopover } from "@/components/pages/resource-explorer/components/app-bar/components/resource-explorer-toolbar/components/ResourceExplorerForecastSuggestionPopover";
import { billingExplorerForecastSuggestionPopoverOpenSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/billingExplorerForecastSuggestionPopoverOpenSelector";
import { explainWithAIAnomaliesSummaryTotalSelector } from "@/store/explain-with-ai/selectors/data/anomalies-summary/explainWithAIAnomaliesSummaryTotalSelector";
import { explainWithAIAnomaliesSummaryAnomaliesSelector } from "@/store/explain-with-ai/selectors/data/anomalies-summary/explainWithAIAnomaliesSummaryAnomaliesSelector";
import { ResourceExplorerFilterSchema } from "@/services/cloudchipr.api";

export const ResourceExplorerToolbar: FC = () => {
  const dispatch = useAppDispatch();
  const enableResourcesExplorerExplainWithAI = useFlag(
    "EnableResourcesExplorerExplainWithAI",
  );
  const enableResourcesExplorerExplainWithAIWithExplanation = useFlag(
    "EnableResourcesExplorerExplainWithAIWithExplanation",
  );

  const dateRangeRef = useRef<HTMLDivElement>(null);
  const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(
    null,
  );

  const forecastSuggestionPopoverOpen = useAppSelector(
    billingExplorerForecastSuggestionPopoverOpenSelector,
  );

  const closeHandler = useCallback(() => {
    dispatch(setForecastSuggestionPopoverOpen(false));
  }, [dispatch]);

  const isResourceExplorerAvailable = useAppSelector(
    isBillingAvailableSelector,
  );

  const anomaliesCount = useAppSelector(
    explainWithAIAnomaliesSummaryTotalSelector,
  );
  const anomalies = useAppSelector(
    explainWithAIAnomaliesSummaryAnomaliesSelector,
  );

  const billingExplorerSettingsPanelOpenHandler = useCallback(() => {
    dispatch(
      setBillingExplorerSideBarPanelContentType("billingExplorerSettings"),
    );
  }, [dispatch]);

  const explainWithAiButtonCLickHandler = useCallback(
    (mode: ResourceExplorerFilterSchema["mode"]) => {
      dispatch(setBillingExplorerSideBarPanelContentType("explainWithAI"));
      dispatch(setBillingExplorerExplainWithAIDataThunk());
      dispatch(setExplainWithAiMode(mode));
    },
    [dispatch],
  );

  useEffect(() => {
    if (dateRangeRef.current) {
      setDateRangeAnchor(dateRangeRef.current);
    }
  }, [dateRangeRef]);

  if (!isResourceExplorerAvailable) {
    return null;
  }

  return (
    <Stack
      p={1}
      direction="row"
      borderBottom={1}
      justifyContent="space-between"
      borderColor="grey.300"
      alignItems="center"
    >
      <Stack direction="row" spacing={1}>
        <BillingExplorerGroupBySelector />

        <div ref={dateRangeRef}>
          <ResourceExplorerDateRange />
        </div>

        <ResourceExplorerForecastSuggestionPopover
          open={forecastSuggestionPopoverOpen}
          onClose={closeHandler}
          anchor={dateRangeAnchor}
        />

        <ResourceExplorerFilterTree />
      </Stack>
      <Stack direction="row" spacing={1}>
        {enableResourcesExplorerExplainWithAI &&
          enableResourcesExplorerExplainWithAIWithExplanation && (
            <ExploreWithAIButtonV2
              onClick={explainWithAiButtonCLickHandler}
              anomaliesCount={anomaliesCount}
              anomalies={anomalies}
            />
          )}

        <IconButton
          onClick={billingExplorerSettingsPanelOpenHandler}
          color="primary"
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
};
